import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TimezoneSelect from "react-timezone-select";
import { ChevronDown } from "lucide-react";
import Button from "./Button";
import { formatLocalDateTime } from "../../utils/dates";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

const MONTH_KEYS = [
  "january","february","march","april","may","june",
  "july","august","september","october","november","december"
];

export default function ScheduleModal({ isOpen, onClose, onSchedule, onSaved, isEditMode = false, accounts = [], defaultTarget = null, initialDate = null }) {
  const { t } = useTranslation();
  const getMonthName = (monthIndex) => t(`calendar.months.${MONTH_KEYS[monthIndex]}`);
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [hour, setHour] = useState(now.getHours());
  const [minute, setMinute] = useState(0);
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [isSaving, setIsSaving] = useState(false);
  const [target, setTarget] = useState(defaultTarget);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [targetError, setTargetError] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const confirmLabel = t("sources.allSet.schedule");

  useEffect(() => {
    if (isOpen) setTarget(defaultTarget);
  }, [isOpen, defaultTarget]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialDate) {
      const d = new Date(initialDate);
      if (!isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
        setSelectedDay(d.getDate());
        setHour(d.getHours());
        setMinute(d.getMinutes());
      }
    } else {
      const now = new Date();
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth());
      setSelectedDay(null);
      setHour(now.getHours());
      setMinute(0);
    }
  }, [isOpen, initialDate]);

  // Auto-dismiss the success state and hand control back to the parent,
  // which decides where to send the user.
  useEffect(() => {
    if (saveStatus !== "success") return;
    const timer = setTimeout(() => {
      setSaveStatus(null);
      setSaveError(null);
      setSelectedDay(null);
      onSaved?.();
    }, 2000);
    return () => clearTimeout(timer);
    // onSaved is deliberately omitted — an inline arrow from the parent
    // changes identity on every render and would keep resetting the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveStatus]);

  if (!isOpen) return null;

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const handlePrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const isToday = (day) =>
    day === now.getDate() && viewMonth === now.getMonth() && viewYear === now.getFullYear();

  const isPast = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return date < today;
  };

  const handleConfirm = async () => {
    const publicationDateTime = formatLocalDateTime(
      new Date(viewYear, viewMonth, selectedDay, hour, minute)
    );

    setIsSaving(true);
    try {
      await onSchedule?.({ publicationDateTime, timezone, target });
      setSaveStatus("success");
    } catch (err) {
      setSaveError(err?.message || null);
      setSaveStatus("error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSaveStatus(null);
    setSaveError(null);
    setSelectedDay(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-[420px] shadow-xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-black-800">
            {isEditMode ? t("saveModal.schedule") : t("navbar.savePost")}
          </h3>
          <button
            onClick={handleClose}
            className="text-black-600 hover:text-black-900 text-xl font-bold p-1"
          >
            ✕
          </button>
        </div>

        {/* ===== SUCCESS STATE ===== */}
        {saveStatus === "success" ? (
          <div className="flex flex-col items-center py-8 gap-4">
            <div className="text-4xl">✅</div>
            <p className="text-lg font-bold text-black-800 text-center">
              {t("saveModal.successTitle")}
            </p>
            <p className="text-black-600 text-center text-sm">
              {t("saveModal.successMessage")}
            </p>
            <button
              onClick={() => {
                setSaveStatus(null);
                setSaveError(null);
                setSelectedDay(null);
                onSaved?.();
              }}
              className="flex flex-1 bg-black-600 hover:bg-black-900 focus:outline-none focus:ring-2 focus:ring-primary-400 text-white text-lg font-bold rounded-lg border-black-600 hover:border-black-900 border-2 items-center justify-center w-full p-2 gap-2 md:text-md md:self-end sm:flex-row sm:items-center sm:flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("buttons.close")}
            </button>
          </div>
        ) : saveStatus === "error" ? (
          /* ===== ERROR STATE ===== */
          <div className="flex flex-col items-center py-8 gap-4">
            <div className="text-4xl">❌</div>
            <p className="text-lg font-bold text-black-800 text-center">
              {t("saveModal.errorTitle")}
            </p>
            <p className="text-black-600 text-center text-sm">
              {saveError || t("saveModal.errorMessage")}
            </p>
            <button
              onClick={() => { setSaveStatus(null); setSaveError(null); }}
              className="mt-4 px-6 py-2 border-2 border-black-600 font-bold rounded-lg hover:bg-black-20"
            >
              {t("buttons.tryAgain")}
            </button>
          </div>
        ) : (
          /* ===== CALENDAR STATE ===== */
          <>
            {/* Info note */}
            <p className="bg-link-100 text-sm sm:text-base lg:text-md text-black-800 px-3 py-2 rounded mb-4">
              {t("saveModal.scheduleNote")}
            </p>
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handlePrevMonth}
                className="w-8 h-8 rounded-full bg-white border border-black-600 flex items-center justify-center hover:bg-primary-200 transition-all font-bold"
              >
                ‹
              </button>
              <span className="font-bold text-black-800">
                {getMonthName(viewMonth)} {viewYear}
              </span>
              <button
                onClick={handleNextMonth}
                className="w-8 h-8 rounded-full bg-white border border-black-600 flex items-center justify-center hover:bg-primary-200 transition-all font-bold"
              >
                ›
              </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center text-xs font-bold text-black-400 py-1">
                  {t(`calendar.weekdays.${d}`)}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 mb-4">
              {cells.map((day, idx) => (
                <div key={idx} className="flex items-center justify-center">
                  {day ? (
                    <button
                      onClick={() => !isPast(day) && setSelectedDay(day)}
                      disabled={isPast(day)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all
                        ${isPast(day)
                          ? "text-black-200 cursor-not-allowed"
                          : selectedDay === day
                            ? "bg-Brand-neon-600 text-white border-Brand-neon-600"
                            : isToday(day)
                              ? "border-2 border-black-800 text-black-800 hover:bg-primary-200"
                              : "bg-white border border-black-400 text-black-800 hover:bg-primary-200"
                        }`}
                    >
                      {day}
                    </button>
                  ) : (
                    <div className="w-10 h-10" />
                  )}
                </div>
              ))}
            </div>

            {/* Time picker */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs font-bold text-black-400 uppercase">{t("scheduleModal.hour")}</span>
                <select
                  value={hour}
                  onChange={(e) => setHour(Number(e.target.value))}
                  className="border-2 border-black-600 rounded-lg px-3 py-2 text-lg font-bold text-black-800 focus:outline-none focus:ring-2 focus:ring-primary-400 w-[80px] text-center"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
                  ))}
                </select>
              </div>
              <span className="text-2xl font-bold text-black-400 mt-4">:</span>
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs font-bold text-black-400 uppercase">{t("scheduleModal.minute")}</span>
                <select
                  value={minute}
                  onChange={(e) => setMinute(Number(e.target.value))}
                  className="border-2 border-black-600 rounded-lg px-3 py-2 text-lg font-bold text-black-800 focus:outline-none focus:ring-2 focus:ring-primary-400 w-[80px] text-center"
                >
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Timezone */}
            <div className="mb-4">
              <label className="text-xs font-bold text-black-400 uppercase block mb-1">{t("scheduleModal.timezone")}</label>
              <TimezoneSelect
                value={timezone}
                onChange={(tz) => setTimezone(tz.value)}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    border: "2px solid",
                    borderColor: state.isFocused ? "#36ee92" : "#2e2e2e",
                    borderRadius: "0.5rem",
                    backgroundColor: "#ffffff",
                    boxShadow: state.isFocused ? "0 0 0 2px #36ee92" : "none",
                    fontSize: "1rem",
                    fontWeight: "500",
                    padding: "0.2rem 0.5rem",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? "#36ee92"
                      : state.isFocused ? "#f5f5f5" : "#ffffff",
                    color: "#0d0d0d",
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: "0.5rem",
                    border: "1px solid #dedede",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "#0d0d0d",
                  }),
                }}
              />
            </div>

            {/* Selected date preview */}
            {selectedDay && (
              <div className="bg-black-20 rounded-lg px-4 py-3 mb-4 text-center">
                <span className="text-black-600 text-sm">{t("scheduleModal.scheduledFor")} </span>
                <span className="font-bold text-black-800">
                  {String(selectedDay).padStart(2, "0")}/{String(viewMonth + 1).padStart(2, "0")}/{viewYear} {t("scheduleModal.at")} {String(hour).padStart(2, "0")}:{String(minute).padStart(2, "0")}
                </span>
              </div>
            )}

            {/* LinkedIn Profile */}
            <div className="mb-4">
              <label className="text-xs font-bold text-black-400 uppercase block mb-1">
                {t("scheduleModal.linkedinProfile")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setIsAccountDropdownOpen(prev => !prev)}
                  className={`!justify-between w-full ${targetError ? "!border-red-500" : ""}`}
                >
                  <span className="font-medium">{accounts.find(a => a.target === target)?.label || t("scheduleModal.selectAccount")}</span>
                  <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
                </Button>
                {isAccountDropdownOpen && (
                  <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <ul className="py-2 text-sm text-gray-700">
                      {accounts.map((acc) => (
                        <li key={acc.target}>
                          <button
                            type="button"
                            onClick={() => { setTarget(acc.target); setTargetError(false); setIsAccountDropdownOpen(false); }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            {acc.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {targetError && (
                <p className="text-red-500 text-xs mt-1">{t("scheduleModal.accountRequired")}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={handleClose}
                className="flex flex-1 w-full bg-black-0 hover:bg-black-20 focus:outline-none focus:ring-2 focus:ring-primary-400 text-black-800 text-lg font-bold rounded-lg border-black-800 border-2 items-center justify-center p-2 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("buttons.cancel")}
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSaving || !selectedDay || !target}
                className="flex flex-1 bg-black-600 hover:bg-black-900 focus:outline-none focus:ring-2 focus:ring-primary-400 text-white text-lg font-bold rounded-lg border-black-600 hover:border-black-900 border-2 items-center justify-center w-full p-2 gap-2 md:text-md md:self-end sm:flex-row sm:items-center sm:flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? `${confirmLabel}...` : confirmLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}